import React, { useState } from 'react';
import { GoalTypeQuestionnaireProps } from './types';
import { GoalQuestionnaireAnswers, GoalType } from '../Milestone_organizer/types';

/**
 * GoalTypeQuestionnaire - Stap 2 van de intake flow
 * 
 * Vaste vragenlijst (geen AI) om goal type te bepalen:
 * 1. Heb je dit doel eerder behaald?
 * 2. Heb je het bij anderen gezien?
 * 3. Voelt het comfortabel of spannend?
 * 4. Weet je hoe je het gaat bereiken?
 * 
 * Evaluatielogica bepaalt suggestie, gebruiker bevestigt altijd zelf.
 */

interface Question {
  id: keyof GoalQuestionnaireAnswers;
  question: string;
  options: { value: string; label: string }[];
}

const QUESTIONS: Question[] = [
  {
    id: 'achievedBefore',
    question: 'Heb je dit doel (of iets vergelijkbaars) al eerder behaald?',
    options: [
      { value: 'yes', label: 'Ja' },
      { value: 'no', label: 'Nee' }
    ]
  },
  {
    id: 'seenInOthers',
    question: 'Heb je dit doel of onderdelen ervan al bij anderen gezien?',
    options: [
      { value: 'yes', label: 'Ja' },
      { value: 'no', label: 'Nee' }
    ]
  },
  {
    id: 'feelingAboutGoal',
    question: 'Voelt dit doel comfortabel of juist spannend/oncomfortabel?',
    options: [
      { value: 'comfortable', label: 'Comfortabel' },
      { value: 'exciting', label: 'Spannend' }
    ]
  },
  {
    id: 'knowsHow',
    question: 'Weet je al HOE je dit doel gaat bereiken?',
    options: [
      { value: 'yes', label: 'Ja' },
      { value: 'partially', label: 'Gedeeltelijk' },
      { value: 'no', label: 'Nee' }
    ]
  }
];

/**
 * Evalueer antwoorden en bepaal gesuggereerd goal type
 * A-type: eerder behaald
 * B-type: niet eerder behaald, wel bekend via anderen
 * C-type: groot, oncomfortabel, nog geen pad
 */
function evaluateGoalType(answers: GoalQuestionnaireAnswers): GoalType {
  // A-type: Je hebt dit eerder gedaan
  if (answers.achievedBefore === 'yes') {
    return 'A';
  }

  // C-type: Onbekend terrein - niet bij anderen gezien OF spannend + weet niet hoe
  if (
    answers.seenInOthers === 'no' ||
    (answers.feelingAboutGoal === 'exciting' && answers.knowsHow === 'no')
  ) {
    return 'C';
  }

  // B-type: Niet eerder behaald, maar wel bekend via anderen
  return 'B';
}

const GoalTypeQuestionnaire: React.FC<GoalTypeQuestionnaireProps> = ({
  onNext,
  onBack
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Partial<GoalQuestionnaireAnswers>>({});

  const handleAnswer = (questionId: keyof GoalQuestionnaireAnswers, value: string) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    // Ga naar volgende vraag of rond af
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Alle vragen beantwoord - evalueer en ga door
      const completeAnswers = newAnswers as GoalQuestionnaireAnswers;
      const suggestedType = evaluateGoalType(completeAnswers);
      onNext(completeAnswers, suggestedType);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      onBack();
    }
  };

  const question = QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  return (
    <div className="max-w-xl mx-auto">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <span>Vraag {currentQuestion + 1} van {QUESTIONS.length}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-orange-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {question.question}
        </h2>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-8">
        {question.options.map((option) => (
          <button
            key={option.value}
            onClick={() => handleAnswer(question.id, option.value)}
            className={`w-full py-4 px-6 text-left rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
              answers[question.id] === option.value
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <span className="text-gray-900 font-medium">{option.label}</span>
          </button>
        ))}
      </div>

      {/* Back button */}
      <div className="flex justify-start">
        <button
          onClick={handleBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          &larr; Terug
        </button>
      </div>
    </div>
  );
};

export default GoalTypeQuestionnaire;
